import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  Box,
  Chip,
  Paper,
  CircularProgress,
  Divider,
  Icon
} from '@mui/material';
import { useApiCall } from '../utils/hooks';
import { authApi } from '../utils/api';

function AuthToken() {
  const [result, setResult] = useState(null);
  const { loading, execute } = useApiCall();

  const testAuthToken = async () => {
    try {
      const data = await execute(() => authApi.getToken());
      setResult({
        success: true,
        status: 'Success',
        data: data
      });
    } catch (error) {
      setResult({
        error: error.message,
        success: false
      });
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
            Auth Token Service
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Get OAuth2 authentication tokens for accessing protected APIs.
          </Typography>
        </CardContent>
      </Card>
        
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            API Documentation
          </Typography>
          <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1, fontFamily: 'monospace' }}>
            <Chip label="GET" color="success" size="small" sx={{ mr: 1 }} />
            <Typography component="span" sx={{ fontWeight: 600 }}>
              /api/auth/token
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" paragraph>
            Retrieves a Bearer token for API authentication using client credentials flow.
          </Typography>
          
          <Typography variant="h6" gutterBottom sx={{ mt: 3, fontWeight: 600 }}>
            Response Format
          </Typography>
          <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
            <pre style={{ margin: 0, fontFamily: 'monospace', fontSize: '0.875rem' }}>{`{
  "success": true,
  "access_token": "eyJhbGciOiJSUzI1NiIsImtpZCI6...",
  "token_type": "Bearer"
}`}</pre>
          </Paper>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            API Tester
          </Typography>
          <Button
            variant="contained"
            onClick={testAuthToken}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <Icon>vpn_key</Icon>}
            sx={{ mb: 2 }}
          >
            {loading ? 'Testing...' : 'Test Auth Token'}
          </Button>
          
          {result && (
            <Box>
              <Divider sx={{ mb: 2 }} />
              {result.error ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  Error: {result.error}
                </Alert>
              ) : (
                <>
                  <Typography variant="body2" gutterBottom>
                    Status: <Chip 
                      label={result.status} 
                      color={result.success ? 'success' : 'error'} 
                      size="small" 
                    />
                  </Typography>
                  <Paper sx={{ p: 2, bgcolor: 'grey.50', mt: 1 }}>
                    <pre style={{ 
                      margin: 0, 
                      fontFamily: 'monospace', 
                      fontSize: '0.875rem',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-all'
                    }}>
                      {JSON.stringify(result.data, null, 2)}
                    </pre>
                  </Paper>
                </>
              )}
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

export default AuthToken;
